/**
 * What Postgres said, as Hasura hands it to an admin caller.
 *
 * A refusal raised by a DEFERRED constraint trigger fails the transaction at
 * COMMIT. Hasura then answers with the generic `postgres tx error` and puts
 * the database's own sentence one level down (measured on Hasura v2, run_sql
 * and GraphQL alike):
 *
 *   { message: 'postgres tx error',
 *     extensions: { internal: { statement: 'COMMIT',
 *       error: { message: '<the sentence>', hint: '<machine code>', status_code: '22000' } } } }
 *
 * The group deposit gate (migration 1791259000000) judges a route acting for a
 * user exactly there, so without this every such refusal reached the screen as
 * "postgres tx error". Statement-time refusals already carry the sentence in
 * `message`; they pass through untouched.
 *
 * `hint` is the gate's machine code (`group_gate:<what>`), read by
 * lib/groups/gate-refusal.ts - never the English sentence.
 *
 * Pure - safe in a route, a service and a test.
 */

export interface PgErrorInfo {
  /** The database's sentence, when Hasura passed one. */
  message: string | null
  /** RAISE ... USING HINT. */
  hint: string | null
  /** SQLSTATE. */
  sqlState: string | null
}

/** An Error that remembers what Postgres attached to it. */
export type ErrorWithPg = Error & { pgHint?: string | null; pgSqlState?: string | null; /** D4R-01: the gate marker's parameter. */ pgParams?: Record<string, string> }

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)

/**
 * D4 review 9 (browser e2e D4-01): a refusal raised at STATEMENT time (a BEFORE
 * trigger - the switch guard, the deposit and fee guards, a person's statement
 * judged by the gate) reaches an admin caller WITHOUT `extensions.internal`:
 * `{ errors: [{ extensions: { path: '$', code: 'data-exception' }, message: '<the sentence>' }] }`
 * (Hasura v2, measured: 347 bytes, no hint). The group gate's messages
 * therefore END with their machine code in brackets - ` [group_gate:<what>]`
 * (migration 1791302000000) - read here when the hint is missing, and taken
 * off every sentence a person reads.
 */
export const GATE_MARKER_RE = /\s*\[(group_gate:[a-z_]+)(?: ([a-z_]+)=([^\]]*))?\]\s*$/

/**
 * The sentence without its trailing gate marker, the marker's hint (or null),
 * and its one parameter when it carries one (` [group_gate:x groups=A, B]` -
 * browser re-test D4R-01: the translated sentence names what the English names).
 */
export function splitGateMarker(message: string | null | undefined): { message: string | null; hint: string | null; params?: Record<string, string> } {
  if (typeof message !== 'string') return { message: null, hint: null }
  const m = GATE_MARKER_RE.exec(message)
  if (!m) return { message, hint: null }
  return { message: message.slice(0, m.index), hint: m[1]!, ...(m[2] ? { params: { [m[2]]: m[3] ?? '' } } : {}) }
}

/** The marker's parameter of an error's message (or of its internal message), if any. */
export function gateParamsOf(error: { message?: string; extensions?: Record<string, unknown> } | null | undefined): Record<string, string> | undefined {
  const own = splitGateMarker(error?.message).params
  if (own) return own
  const internal = error?.extensions?.internal as Record<string, unknown> | undefined
  const inner = internal?.error as Record<string, unknown> | undefined
  return splitGateMarker(typeof inner?.message === 'string' ? inner.message : null).params
}

/** Read Hasura's `internal.error` object (run_sql: `internal.error`; GraphQL: `extensions.internal.error`). */
export function pgErrorInfo(internalError: unknown): PgErrorInfo {
  const o = internalError && typeof internalError === 'object' ? (internalError as Record<string, unknown>) : {}
  const split = splitGateMarker(text(o.message))
  return { message: text(split.message), hint: text(o.hint) ?? split.hint, sqlState: text(o.status_code) }
}

/** `extensions.internal.error` of one GraphQL error, read safely - the gate marker of its message when Hasura gave no hint. */
export function graphqlPgErrorInfo(error: { message?: string; extensions?: Record<string, unknown> } | null | undefined): PgErrorInfo {
  const internal = error?.extensions?.internal
  const info = pgErrorInfo(internal && typeof internal === 'object' ? (internal as Record<string, unknown>).error : null)
  if (info.hint) return info
  const marker = splitGateMarker(error?.message).hint
  return marker ? { ...info, hint: marker, sqlState: info.sqlState ?? '22000' } : info
}

/**
 * One GraphQL error as an admin caller hands it on: the sentence a person reads
 * (no gate marker, the database's own sentence when Hasura hid it), and the
 * hint kept in `extensions.internal.error` (written there from the marker when
 * Hasura sent none), so every reader of `graphqlPgErrorInfo` finds the code.
 */
export function normalizedGqlError<E extends { message: string; extensions?: Record<string, unknown> }>(error: E): E {
  const info = graphqlPgErrorInfo(error)
  const message = surfacedMessage(error)
  const params = gateParamsOf(error)
  if (!info.hint) return { ...error, message }
  const internal = (error.extensions?.internal && typeof error.extensions.internal === 'object' ? error.extensions.internal : {}) as Record<string, unknown>
  const inner = (internal.error && typeof internal.error === 'object' ? internal.error : {}) as Record<string, unknown>
  return {
    ...error,
    message,
    extensions: {
      ...(error.extensions ?? {}),
      internal: { ...internal, error: { ...inner, message: inner.message ?? message, hint: info.hint, status_code: inner.status_code ?? info.sqlState } },
      ...(params ? { gateParams: params } : {}),
    },
  }
}

/** SQLSTATE classes whose message is written for a person (data exception, integrity constraint). */
const HUMAN_CLASS = /^(22|23)/

/**
 * The message to show for a GraphQL error: the database's sentence when Hasura
 * hid it behind its generic transaction error, else the message as it is.
 */
export function surfacedMessage(error: { message: string; extensions?: Record<string, unknown> }): string {
  const pg = graphqlPgErrorInfo(error)
  const own = splitGateMarker(error.message).message ?? error.message
  return pg.message && pg.sqlState && HUMAN_CLASS.test(pg.sqlState) && own !== pg.message ? pg.message : own
}

/** An Error carrying the database's hint and SQLSTATE beside its message (the gate marker read and taken off). */
export function pgError(message: string, info: Pick<PgErrorInfo, 'hint' | 'sqlState'>, params?: Record<string, string>): ErrorWithPg {
  const split = splitGateMarker(message)
  const p = params ?? split.params
  return Object.assign(new Error(split.message ?? message), {
    pgHint: info.hint ?? split.hint, pgSqlState: info.sqlState ?? (split.hint ? '22000' : null), ...(p ? { pgParams: p } : {}),
  })
}

/** The gate marker's parameter an error carries (D4R-01), or undefined. */
export function pgParamsOf(e: unknown): Record<string, string> | undefined {
  const p = e && typeof e === 'object' ? (e as { pgParams?: unknown }).pgParams : undefined
  return p && typeof p === 'object' ? (p as Record<string, string>) : undefined
}

/** The hint an error carries, or null. */
export function pgHintOf(e: unknown): string | null {
  return e && typeof e === 'object' ? text((e as { pgHint?: unknown }).pgHint) : null
}
